import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { UrgencyType } from "@src/appV2/Shifts/types";
import { z } from "zod";

const shiftBasicDetailsSchema = z.object({
  _id: z.string(),
  start: z.string(),
  end: z.string(),
  agentId: z.string().optional(),
  agent: z.object({ name: z.string().optional() }).optional(),
  agentReq: z.string().optional(),
  urgency: z.nativeEnum(UrgencyType).optional(),
  canRequestReplacement: z.boolean().optional(),
  facility: z
    .object({
      tmz: z.string().optional(),
      fullAddress: z.object({ state: z.string().optional() }).optional(),
    })
    .optional(),
  attendance: z
    .object({
      arrivedFacilityGeofenceAt: z.string().nullable().optional(),
      leftFacilityGeofenceAt: z.string().nullable().optional(),
    })
    .optional(),
  clockInOut: z
    .object({
      start: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

const getShiftBasicDetailsResponseSchema = z.object({
  success: z.boolean(),
  response: shiftBasicDetailsSchema,
  error: z.unknown().nullable(),
});

export type ShiftBasicDetails = z.infer<typeof shiftBasicDetailsSchema>;
export type GetShiftBasicDetailsResponse = z.infer<typeof getShiftBasicDetailsResponseSchema>;

interface UseGetShiftBasicDetailsParams {
  shiftId: string;
}

/**
 * Fetches basic shift details (start, end, timezone) for FCM request messages.
 * Uses the v1 shift details endpoint which returns minimal shift information.
 */
export function useGetShiftBasicDetails(
  params: UseGetShiftBasicDetailsParams,
  options: UseGetQueryOptions<GetShiftBasicDetailsResponse> = {}
) {
  const { shiftId } = params;

  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/v1/shifts/myShifts/details/${shiftId}`,
    responseSchema: getShiftBasicDetailsResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_SHIFT_FAILURE,
    },
    ...options,
  });
}
